/**
 * Vendors API
 * All vendor-related API operations
 */

import { getZohoRefreshToken } from "../../../../shared/data/storage/authStorage";
import { getStoredCompanyData, getStoredToken } from "../../../../services/api";
import { createApiError } from "../../../../utils/errorHandling";
import {
  VendorDataDTO,
  VendorResponseDTO,
  VendorSearchResponseDTO,
  VendorDetailResponseDTO,
  VendorSearchParamsDTO,
} from "../models/VendorDTOs";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Create a new vendor
 */
export const createVendor = async (
  vendorData: Partial<VendorDataDTO>
): Promise<VendorResponseDTO> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  
  // Add Zoho refresh token
  const vendorDataWithToken = {
    ...vendorData,
    refresh_token: getZohoRefreshToken() || undefined,
  };

  const response = await fetch(`${API_BASE_URL}/api/bills/vendors`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      vendorData: vendorDataWithToken,
      company_gst_enabled: companyData.companyGstEnabled,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to create vendor",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

/**
 * Update an existing vendor
 */
export const updateVendor = async (
  vendorId: string,
  vendorData: Partial<VendorDataDTO>
): Promise<VendorResponseDTO> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  
  // Add Zoho refresh token
  const vendorDataWithToken = {
    ...vendorData,
    refresh_token: getZohoRefreshToken() || undefined,
  };

  const response = await fetch(
    `${API_BASE_URL}/api/bills/vendors/${vendorId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        vendorData: vendorDataWithToken,
        company_gst_enabled: companyData.companyGstEnabled,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to update vendor",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

/**
 * Search vendors with pagination
 */
export const searchVendors = async (
  params: VendorSearchParamsDTO = {}
): Promise<VendorSearchResponseDTO> => {
  const token = getStoredToken();

  // Build query parameters.
  // GET /api/bills/vendors paginates by offset (not page). Convert page → offset
  // when callers still pass page so pagination stays correct.
  const queryParams = new URLSearchParams();
  const limit = params.limit ?? 20;
  const offset =
    params.offset !== undefined
      ? params.offset
      : params.page !== undefined
        ? Math.max(0, (params.page - 1) * limit)
        : undefined;

  if (offset !== undefined) queryParams.append("offset", offset.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status) queryParams.append("status", params.status);

  const url = `${API_BASE_URL}/api/bills/vendors${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search vendors",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

/**
 * Get vendor by ID
 */
export const getVendorById = async (
  vendorId: string
): Promise<VendorDetailResponseDTO> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bills/vendors/${vendorId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch vendor details",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

